import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import {
  DataGridPremium,
  GridRowModel,
  GridFeatureMode,
  GridFilterModel,
} from "@mui/x-data-grid-premium";
import {
  Tooltip,
  IconButton,
  Box,
  Button,
  Typography,
  Backdrop,
  CircularProgress,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import IncidentUserandStatusDialog from "../Molecules/Rule/IncidentUserandStatusDialog";
import {
  IconBoltType,
  IconIncidentDetails,
  IconIncidentResources,
} from "../../common/utils/icons";
import DashboardUserDetails from "../Molecules/Rule/DashboardUserDetails";
import {
  capitalize,
  getFormattedDateTime,
  getSanatizedCode,
  calculateDaysInQueue,
} from "../../services/utils";
import useFetchUsersList from "../../hooks/useFetchUsersList";
import { useLocation, useParams } from "react-router-dom";
import StatusCell from "../../common/utils/statusIcon";
import IssuesListSnapShot from "../Molecules/IssuesList/IssuesListSnapShot";
import {
  ClassificationColumn,
  CommentColumn,
  CurrentStatusColumn,
  ReasonColumn,
  UserColumn,
} from "../../common/utils/commonActionsFields";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import DataGridPaginationFooter from "./DataGridPaginationFooter";

interface IssueListDataGridProps {
  setUserDialogData: any;
  userDialogData: any;
  isLoading?: any;
  setIsLoading?: any;
  searchFilterData?: any;
  fetchedIssuesList: any;
  currentPageType: any;
  isShowEditIssue?: boolean;
  paginationModel?: any;
  onPaginationModelChange?: (params: any) => void;
  paginationMode?: GridFeatureMode;
  rowCount: any;
  pageSizeOptions?: number[];
  isFilterHide?: boolean;
  /**
   * Optional: Selected row IDs passed from parent component.
   * If provided, the grid will use this as the source of truth (controlled mode).
   * If not provided, the grid will manage its own local state (uncontrolled mode).
   */
  selectedIds?: number[];
  /**
   * Optional: Callback function to update selected row IDs in parent component.
   * Should be provided together with selectedIds for controlled mode.
   * If not provided, the grid will manage its own local state (uncontrolled mode).
   */
  setSelectedIds?: (ids: number[]) => void;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}
const initialDashboardData = {
  status: false,
  assigned_user: "",
  issueId: null,
  isBackDropVisible: false,
};

const initialSnapShotData = {
  isVisible: false,
  snapShotData: [],
  isBackDropVisible: false,
};

const CustomHeaderFilter = React.memo(
  ({ column, filterValue, onFilterChange, isFilterHide }: any) => {
    const [inputValue, setInputValue] = useState(filterValue || "");

    // Create a stable debounced function that doesn't depend on onFilterChange reference
    const debouncedFilterChange = useMemo(
      () =>
        debounce((value: any) => {
          // Always call onFilterChange with the current value - let handleFilterChange decide
          const currentValue = value || "";
          onFilterChange(currentValue);
        }, 200), // Reduced debounce time for better responsiveness
      [column?.field] // Only depend on field, not filterValue
    );

    useEffect(() => {
      setInputValue(filterValue || "");
    }, [filterValue]);

    useEffect(() => {
      // Always call the debounced function when inputValue changes
      debouncedFilterChange(inputValue);

      return () => {
        debouncedFilterChange.cancel();
      };
    }, [inputValue, debouncedFilterChange]);

    const rawHeader = column?.headerName;
    const headerText =
      typeof rawHeader === "string"
        ? rawHeader
        : typeof column?.field === "string"
        ? column.field
        : "";
    const shortLabel =
      headerText && headerText.length > 15
        ? `${headerText.slice(0, 15)}...`
        : headerText;

    return (
      <Box display="flex" flexDirection="column">
        <Tooltip title={headerText} placement="top" arrow>
          <Box className="header-name">{shortLabel}</Box>
        </Tooltip>
        <Box className={`textbox-group-wrapper ${isFilterHide ? "hide" : ""}`}>
          <Box className={`textbox-group`}>
            <TextField
              variant="outlined"
              size="small"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={`Filter ${headerText}`}
            />
            <IconButton>
              <SearchIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }
);

const IssueListDataGrid = ({
  setUserDialogData,
  userDialogData,
  isLoading,
  setIsLoading,
  searchFilterData,
  fetchedIssuesList,
  currentPageType,
  isShowEditIssue = false,
  paginationModel,
  onPaginationModelChange,
  paginationMode = "client",
  rowCount,
  pageSizeOptions,
  isFilterHide = false,
  // State management props from parent component (optional)
  // If both are provided, the grid operates in "controlled mode" (parent manages state)
  // If not provided, the grid operates in "uncontrolled mode" (grid manages its own state)
  selectedIds: parentSelectedIds = [],
  setSelectedIds: onParentSelectedIdsChange,
}: IssueListDataGridProps) => {
  const { ruleResultId }: any = useParams();
  const apiRef = useRef<GridApiPremium>(null!);
  const { rowsPerPage, rowsPerPageOptions } = useRowsPerPage();

  const [detailViewDataRows, setDetailViewDataRows] = useState<any>([]);
  const [detailViewDataColumns, setDetailViewDataColumns] = useState<any>([]);

  // ============================================================================
  // STATE MANAGEMENT: Controlled vs Uncontrolled Mode
  // ============================================================================
  // This component supports two modes:
  //
  // 1. CONTROLLED MODE (when parent provides selectedIds + onParentSelectedIdsChange):
  //    - Parent component manages the selectedIds state
  //    - Grid uses the parent's state as the source of truth
  //    - When user selects rows, grid calls onParentSelectedIdsChange to notify parent
  //    - Parent updates its state and passes new selectedIds back to grid
  //
  // 2. UNCONTROLLED MODE (when parent doesn't provide these props):
  //    - Grid manages its own local selectedIds state
  //    - Grid is self-contained and doesn't need parent involvement
  //    - Useful for standalone grid usage
  // ============================================================================

  // Local state for uncontrolled mode (only used if parent doesn't provide state management)
  const [localSelectedIds, setLocalSelectedIds] = useState<any>([]);

  // Determine which selectedIds to use: parent's or local?
  // If parent provided selectedIds, use it (controlled mode)
  // Otherwise, use local state (uncontrolled mode)
  const selectedIds =
    parentSelectedIds && parentSelectedIds.length >= 0
      ? parentSelectedIds
      : localSelectedIds;

  /**
   * Unified setter function that works in both controlled and uncontrolled modes.
   *
   * In CONTROLLED MODE: Calls the parent's callback (onParentSelectedIdsChange)
   * In UNCONTROLLED MODE: Updates the local state (setLocalSelectedIds)
   *
   * This function handles both:
   * - Direct values: setSelectedIds([1, 2, 3])
   * - Functional updates: setSelectedIds(prev => [...prev, 4])
   */
  const setSelectedIds = (value: any) => {
    // Check if we're in controlled mode (parent provided a callback)
    if (onParentSelectedIdsChange) {
      // CONTROLLED MODE: Use parent's callback
      if (typeof value === "function") {
        // If value is a function, call it with current selectedIds to get the new value
        const newValue = value(selectedIds);
        // Then pass the new value to parent
        onParentSelectedIdsChange(newValue);
      } else {
        // If value is a direct value, pass it directly to parent
        onParentSelectedIdsChange(value);
      }
    } else {
      // UNCONTROLLED MODE: Use local state
      if (typeof value === "function") {
        // React's setState accepts functions, so pass it directly
        setLocalSelectedIds(value);
      } else {
        // Direct value, pass it directly
        setLocalSelectedIds(value);
      }
    }
  };
  //const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allUsersList, setallUsersList] = useState<any>([]);
  const [keyColumns, setKeyColumns] = useState<any>([]);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(false);
  const [currentExecutionId, setCurrentExecutionId] = useState<
    number | null | undefined
  >(null);

  // execution id is static
  const dataGridProps = {
    // ... other props
    paginationMode,
    pagination: paginationMode !== "client",
    ...(paginationMode !== "client"
      ? {
          rowCount,
          pageSizeOptions: pageSizeOptions || rowsPerPageOptions,
          paginationModel,
          onPaginationModelChange,
        }
      : {
          pagination: true, // Set to false for client-side pagination
          pageSize: paginationMode === "client" ? rowsPerPage : undefined,
          pageSizeOptions:
            paginationMode === "client"
              ? pageSizeOptions || rowsPerPageOptions
              : undefined,
        }),
  };

  const [dashboardUserDetailsData, setDashboardUserDetailsData] =
    useState(initialDashboardData);
  const [snapShotData, setSnapShotData] = useState(initialSnapShotData);

  // Filter state management
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [filteredDataRows, setFilteredDataRows] = useState<any[]>([]);

  //commented this code only to check validation
  const getRecordDetailsForKey = (items: any[], keyToUse: string) => {
    const item = items[0];
    if (keyToUse === "comparison_keys") {
      return (
        item?.mismatched_record_details?.[keyToUse] ||
        item?.missing_record_details?.[keyToUse]
      );
    } else {
      return (
        item?.validation_error_record_details?.[keyToUse] ||
        item?.null_key_error_record_details?.[keyToUse] ||
        item?.duplicate_record_details?.[keyToUse]
      );
    }
  };

  useEffect(() => {
    if (fetchedIssuesList && fetchedIssuesList?.items?.length > 0) {
      const keyToUse =
        currentPageType === "execution" ? "comparison_keys" : "resource_keys";
      const rawData = getRecordDetailsForKey(
        fetchedIssuesList?.items || [],
        keyToUse
      );
      if (rawData) {
        try {
          //const comparisonKeysObj = JSON.parse(rawData);
          if (rawData) {
            const keys = Object.keys(rawData);
            setKeyColumns(keys);
          }
        } catch (error) {
          console.error("Error parsing JSON:", error);
        }
      }
    }
    if (dashboardUserDetailsData?.status === true) {
      setDashboardUserDetailsData(initialDashboardData);
    }
    if (snapShotData?.isVisible === true) {
      setSnapShotData(initialSnapShotData);
    }
  }, [fetchedIssuesList, currentPageType]);

  const [usersList] = useFetchUsersList({
    setIsLoading: setIsUserLoading,
  });
  useEffect(() => {
    setCurrentExecutionId(ruleResultId);
  }, [ruleResultId]);

  useEffect(() => {
    setallUsersList(usersList);
  }, [usersList]);

  // Filter application logic
  useEffect(() => {
    const applyFilters = () => {
      let filteredRows = [...detailViewDataRows];

      // Only apply filters if there are active filter items
      if (filterModel.items && filterModel.items.length > 0) {
        filterModel.items.forEach((filter) => {
          const { field, value } = filter;
          const lowerCaseValue = value?.toString().toLowerCase().trim() || "";

          // Only filter if the value is not empty
          if (lowerCaseValue) {
            filteredRows = filteredRows.filter((row) => {
              const cellValue = row[field]?.toString().toLowerCase() || "";
              return cellValue.includes(lowerCaseValue);
            });
          }
        });
      }

      setFilteredDataRows(filteredRows);
    };

    applyFilters();
  }, [filterModel, detailViewDataRows]);

  const handleFilterChange = useCallback((field: string, value: string) => {
    setFilterModel((prevFilterModel) => {
      const newFilterItems = prevFilterModel.items.filter(
        (item) => item.field !== field
      );

      // Only add filter if value is not empty/whitespace
      const trimmedValue = (value || "").trim();
      const shouldAddFilter = trimmedValue.length > 0;
      if (shouldAddFilter) {
        newFilterItems.push({
          field: field,
          operator: "contains",
          value: trimmedValue,
        });
      }

      // Only update if the filter actually changed
      const hasChanged =
        JSON.stringify(prevFilterModel.items) !==
        JSON.stringify(newFilterItems);
      if (!hasChanged) {
        return prevFilterModel;
      }

      return { items: newFilterItems };
    });
  }, []); // No dependencies needed since we use functional updates

  // Memoize filter change handlers per column to prevent re-renders
  const filterChangeHandlers = useMemo(() => {
    const handlers: { [key: string]: (value: any) => void } = {};
    return handlers;
  }, []);

  const getFilterChangeHandler = useCallback(
    (field: string) => {
      if (!filterChangeHandlers[field]) {
        filterChangeHandlers[field] = (value: any) =>
          handleFilterChange(field, value);
      }
      return filterChangeHandlers[field];
    },
    [filterChangeHandlers, handleFilterChange]
  );

  // Function to apply filters to columns - memoized to prevent unnecessary re-renders
  const applyFiltersToColumns = useCallback(
    (columns: any[]) => {
      return columns.map((column) => ({
        ...column,
        sortable: false,
        renderHeader: (params: any) => (
          <CustomHeaderFilter
            column={params.colDef}
            filterValue={
              filterModel.items.find((item) => item.field === column.field)
                ?.value || ""
            }
            onFilterChange={getFilterChangeHandler(column.field)}
            isFilterHide={isFilterHide}
          />
        ),
      }));
    },
    [filterModel.items, getFilterChangeHandler, isFilterHide]
  );
  const getFormattedKeysPairs = (inputString: string | undefined) => {
    const pairs: any = {};

    if (inputString && typeof inputString === "string") {
      const cleanedString = inputString.trim().slice(1, -1);
      const cleanedWithoutQuotes =
        cleanedString.startsWith('"') && cleanedString.endsWith('"')
          ? cleanedString.slice(1, -1)
          : cleanedString;

      cleanedWithoutQuotes.split(",").forEach((pair) => {
        if (pair.trim() !== "None") {
          let [key, value] = pair.trim().split(":");
          key = key.replace(/^"|"$/g, "").trim();
          if (key && value) {
            pairs[key.trim()] = getSanatizedCode(value.trim());
          }
        }
      });
    }

    return pairs;
  };

  // Memoize the processed data to prevent unnecessary recalculations
  const processedDetailViewData = useMemo(() => {
    if (fetchedIssuesList?.items?.length > 0 && currentPageType !== "") {
      return fetchedIssuesList?.items?.map((detail: any) => {
        const baseData = {
          ...detail,
          rowId: detail.id,
        };
        const diffInDays = calculateDaysInQueue(detail.first_occurrence);

        // Process data based on page type
        if (currentPageType === "execution") {
          // Execution data processing
          return {
            ...baseData,
            comparison_keys: getFormattedKeysPairs(detail.comparison_keys),
            additional_info: getFormattedKeysPairs(detail.additional_info),
          };
        } else if (currentPageType === "validation") {
          // Validation data processing
          return {
            ...baseData,
            resource_keys: getFormattedKeysPairs(detail.resource_keys),
          };
        } else if (currentPageType === "genericResearch") {
          // Generic research data processing - spread the research query result
          const queryName =
            detail?.generic_research_record_details?.query_name || "";
          const researchResult =
            detail?.generic_research_record_details?.research_query_result ||
            {};
          return {
            ...baseData,
            name: queryName,
            days_in_queue: diffInDays,
            ...researchResult, // Spread the research query result data
          };
        } else if (currentPageType === "ruleResearch") {
          // Rule research data processing - get data from comparison_research_record_details
          const queryName =
            detail?.comparison_research_record_details?.research_query_results
              ?.name || "";

          const researchResult =
            detail?.comparison_research_record_details?.research_query_results
              ?.result || {};
          return {
            ...baseData,
            days_in_queue: diffInDays,
            name: queryName,
            ...researchResult, // Spread the research query result data
          };
        } else {
          // Fallback
          return baseData;
        }
      });
    }
    return [];
  }, [fetchedIssuesList, currentPageType]);

  useEffect(() => {
    setDetailViewDataRows(processedDetailViewData);
    if (processedDetailViewData?.length > 0) {
      apiRef?.current &&
        apiRef?.current?.resetRowHeights &&
        apiRef?.current?.resetRowHeights();
    } else {
      setDetailViewDataColumns([]);
    }
  }, [processedDetailViewData]);

  useEffect(() => {
    if (
      // lorem
      detailViewDataRows?.length > 0 &&
      (detailViewDataRows[0]?.mismatched_record_details?.comparison_keys ||
        detailViewDataRows[0]?.missing_record_details?.comparison_keys ||
        detailViewDataRows[0]?.validation_error_record_details?.resource_keys ||
        detailViewDataRows[0]?.null_key_error_record_details ||
        detailViewDataRows[0]?.duplicate_record_details?.resource_keys ||
        detailViewDataRows[0]?.comparison_research_record_details ||
        detailViewDataRows[0]?.generic_research_record_details)
    ) {
      getKeyColumns(detailViewDataRows);
      getKeyRows(detailViewDataRows);
    }
  }, [detailViewDataRows]);

  const processRows = (rows: any[]) => {
    if (!rows) return;
    return rows.map((row: any) => {
      const updatedRow = { ...row };

      Object.keys(updatedRow).forEach((key) => {
        if (typeof updatedRow[key] === "string") {
          if (
            /\.0$/.test(updatedRow[key]) &&
            !/\.0[1-9]/.test(updatedRow[key])
          ) {
            updatedRow[key] = updatedRow[key].slice(0, -2);
          }
        }
      });

      return updatedRow;
    });
  };
  const getRecordDetails = (item: any, key: string, currentPageType: any) => {
    return currentPageType === "execution"
      ? item?.mismatched_record_details?.[key] ||
          item?.missing_record_details?.[key]
      : item?.validation_error_record_details?.[key] ||
          //item?.null_key_error_record_details?.[key] ||
          item?.duplicate_record_details?.[key];
  };

  const getKeyRows = (missing_records_details: any) => {
    const rows: any = missing_records_details?.map((item: any, idx: any) => {
      const diffInDays = calculateDaysInQueue(item.first_occurrence);
      const recordDetails = getRecordDetails(
        item,
        currentPageType === "execution" ? "comparison_keys" : "resource_keys",
        currentPageType
      );

      return {
        id: item?.id,
        rowId: item?.id,

        snapShot:
          item?.mismatched_record_details?.record_snapshot?.record_data ||
          item?.missing_record_details?.record_snapshot?.record_data ||
          item?.validation_error_record_details?.record_snapshot?.record_data ||
          item?.null_key_error_record_details?.record_snapshot?.record_data ||
          item?.duplicate_record_details?.record_snapshot?.record_data,
        keys: { ...recordDetails },
        rule_id: item?.rule_id,
        execution_id: item?.execution_id,
        error_message:
          item?.mismatched_record_details?.error_message ||
          item?.missing_record_details?.error_message ||
          item?.validation_error_record_details?.error_message ||
          item?.null_key_error_record_details?.error_message ||
          item?.duplicate_record_details?.error_message,

        issue_type: item?.issue_type,
        first_occurrence: item?.first_occurrence,
        days_in_queue: diffInDays,
        current_status: item?.current_status,
        comment: item?.comment,
        assigned_user: item?.assigned_user,
        create_date: item?.create_date,
        issue_classification_id: item?.issue_classification_id,
        issue_reason_id: item?.issue_reason_id,
        domain_name: item?.domain_record_details?.domain_name,
        name:
          currentPageType === "execution"
            ? item?.rule_record_details?.name
            : item?.resource_record_details?.resource_name,
        execution_name: item?.execution_record_details?.execution_name,
      };
    });
    const processedRows = processRows(rows);
    if (currentPageType === "execution" || currentPageType === "validation") {
      if (processedRows && processedRows?.length > 0) {
        const updatedRows = processedRows?.map((row: any, index: number) => ({
          ...row,
        }));
        setDetailViewDataRows(updatedRows);
      }
      // remove to here

      //setDetailViewDataRows(processedRows);
    }
    apiRef?.current &&
      apiRef?.current?.resetRowHeights &&
      apiRef?.current?.resetRowHeights();
  };

  // Column definitions for different issue types
  const getExecutionValidationColumns = () => [
    {
      field: "keys",
      headerName: "Keys",
      flex: 1,
      minWidth: 250,
      renderCell: (params: any) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html:
                params &&
                params?.row?.keys &&
                Object.entries(params?.row?.keys).length > 0
                  ? Object.entries(params?.row?.keys)
                      .map(([key, value]: any, index: any) => {
                        return (
                          `${key}: ${value}` +
                          (index !==
                          Object.entries(params?.row?.keys).length - 1
                            ? "<br />"
                            : "")
                        );
                      })
                      .join("")
                  : "N/A",
            }}
          />
        );
      },
    },
    {
      field: "issue_type",
      headerName: "Issue Type",
      flex: 1,
      minWidth: 120,
      renderCell: (params: any) => {
        return <Box>{capitalize(params.value)}</Box>;
      },
    },
    {
      field: "domain_name",
      headerName: "Domain Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => {
        return <Box className="word-break-all">{params.value}</Box>;
      },
    },
    {
      field: "execution_name",
      headerName: "Execution Name",
      flex: 1,
      minWidth: 250,
      renderCell: (params: any) => {
        return <Box className="word-break-all">{params.value}</Box>;
      },
    },
    {
      field: "name",
      headerName:
        currentPageType === "execution" ? "Rule Name" : "Resource Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        return <Box className="word-break-all">{params.value}</Box>;
      },
    },
    {
      field: "error_message",
      headerName: "Error Message",
      minWidth: 500,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.value} placement="top">
            <Box>{params.value}</Box>
          </Tooltip>
        );
      },
    },
    {
      field: "days_in_queue",
      headerName: "Age",
      flex: 1,
      minWidth: 110,
      renderCell: (params: any) => {
        return (
          <Box>
            {params?.row?.first_occurrence === null ? (
              params.value ?? 0
            ) : (
              <Tooltip
                title={
                  <Typography
                    sx={{ fontSize: "0.875rem" }}
                  >{`First Occurrence : ${getFormattedDateTime(
                    params?.row?.first_occurrence
                  )}`}</Typography>
                }
                placement="top"
                className="tooltip-ellipsis"
              >
                {params.value ?? 0}
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    CurrentStatusColumn({}),
    UserColumn({}),
    CommentColumn({}),
    ClassificationColumn({}),
    ReasonColumn({}),
  ];

  const getResearchColumns = () => {
    // If data is still loading, return empty array to prevent unexpected columns
    if (isLoading) {
      return [];
    }

    // Check if we have valid data with research results
    if (fetchedIssuesList?.items?.length > 0) {
      // Create columns from the first research result object
      let firstResult;

      if (currentPageType === "ruleResearch") {
        firstResult =
          fetchedIssuesList?.items[0]?.comparison_research_record_details
            ?.research_query_results?.result;
      } else if (currentPageType === "genericResearch") {
        firstResult =
          fetchedIssuesList?.items[0]?.generic_research_record_details
            ?.research_query_result;
      }

      // Only proceed if we have valid research result data
      if (
        !firstResult ||
        typeof firstResult !== "object" ||
        Object.keys(firstResult).length === 0
      ) {
        // If no valid research results, return empty array to prevent unexpected columns
        return [];
      }

      let dynamicColumns: any[] = [];

      dynamicColumns = Object.keys(firstResult).map((key) => ({
        field: key,
        headerName: (
          <Tooltip title={`${key}`} placement={"top"} arrow>
            <span>{`${key.slice(0, 15)}${
              key.length >= 15 ? "..." : ""
            } `}</span>
          </Tooltip>
        ),
        width: 160,
      }));

      const queryNameAndId: any[] = [
        {
          field: "name",
          headerName: "Query Name",
          width: 120,
          minWidth: 150,
          flex: 1,
          renderCell: (params: any) => {
            return <>{params?.row?.name}</>;
          },
        },
        {
          field: "execution_id",
          headerName: "Execution ID",
          width: 120,
          minWidth: 150,
          flex: 1,
          renderCell: (params: any) => {
            return (
              <>
                {currentPageType === "ruleResearch"
                  ? params?.row?.rule_execution_id
                  : params?.row?.generic_execution_id}
              </>
            );
          },
        },
      ];
      const firstOccurrence: any[] = [
        {
          field: "days_in_queue",
          headerName: "Age",
          flex: 1,
          minWidth: 110,
          renderCell: (params: any) => {
            return (
              <Box>
                {params?.row?.first_occurrence === null ? (
                  params.value ?? 0
                ) : (
                  <Tooltip
                    title={
                      <Typography
                        sx={{ fontSize: "0.875rem" }}
                      >{`First Occurrence : ${getFormattedDateTime(
                        params?.row?.first_occurrence
                      )}`}</Typography>
                    }
                    placement="top"
                    className="tooltip-ellipsis"
                  >
                    {params.value ?? 0}
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
      ];

      // Add issue management columns
      const issueColumns = [
        CurrentStatusColumn({}),
        UserColumn({}),
        CommentColumn({}),
        ClassificationColumn({}),
        ReasonColumn({}),
      ];

      return [
        ...queryNameAndId,
        ...dynamicColumns,
        ...firstOccurrence,
        ...issueColumns,
      ];
    }

    // If not loading but no data available, return empty array
    // This prevents the "unexpected columns" behavior
    return [];
  };

  const getKeyColumns = (missing_records_details: any) => {
    const keyToUse =
      currentPageType === "execution" ? "comparison_keys" : "resource_keys";

    // Get record details for processing (used internally by the grid)
    getRecordDetails(missing_records_details[0], keyToUse, currentPageType);

    // Choose column configuration based on issue type

    let columns;
    if (currentPageType === "execution" || currentPageType === "validation") {
      columns = getExecutionValidationColumns();
    } else if (
      currentPageType === "ruleResearch" ||
      currentPageType === "genericResearch"
    ) {
      columns = getResearchColumns();
    } else {
      // Fallback to execution/validation columns
      columns = getExecutionValidationColumns();
    }

    // Add action column to all configurations
    const actionColumn = {
      field: "action",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      minWidth: "130",
      pinned: "right",
      renderCell: (params: any) => {
        return (
          <>
            <Box sx={{ display: "inline-flex", columnGap: "12px" }}>
              <Tooltip title={`New Issue Details`} placement="top" arrow>
                <Button className="btn-blue btn-orange btn-sm">
                  <IconBoltType />
                </Button>
              </Tooltip>
              {isShowEditIssue && (
                <Tooltip
                  title={`Issue Details : ${params?.row?.id}`}
                  placement="top"
                  arrow
                >
                  <Button
                    className="btn-blue btn-orange btn-sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDashboardUserDetailsData((prev: any) => ({
                        ...prev,
                        status: true,
                        incidentId: params.row.rowId,
                        assigned_user: params.row.assigned_user,
                        panelType: "incidentDetail",
                        isBackDropVisible: true,
                        first_occurrence: params.row.first_occurrence,
                        days_in_queue: params.row.days_in_queue,
                        issue_classification_id:
                          params.row.issue_classification_id,
                        issue_reason_id: params.row.issue_reason_id,
                        comment: [
                          {
                            comment: params.row.comment,
                            create_date: params.row.create_date,
                            assigned_user: params.row?.assigned_user || "",
                          },
                        ],
                        current_status: params.row.current_status,
                        issueId: params.row.id,
                      }));
                    }}
                  >
                    <IconIncidentDetails />
                  </Button>
                </Tooltip>
              )}
              {(currentPageType === "execution" ||
                currentPageType === "validation") && (
                <Tooltip title="SnapShot Records" placement="top" arrow>
                  <Button
                    className="btn-blue btn-orange btn-sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSnapShotData((prev: any) => ({
                        ...prev,
                        isVisible: true,
                        isBackDropVisible: true,
                        snapShotData: params?.row?.snapShot,
                      }));
                    }}
                  >
                    <IconIncidentResources />
                  </Button>
                </Tooltip>
              )}
            </Box>
          </>
        );
      },
    };

    const columnsWithFilters = applyFiltersToColumns([
      ...columns,
      actionColumn,
    ]);
    setDetailViewDataColumns(columnsWithFilters);
  };

  React.useEffect(() => {
    setUserDialogData((prev: any) => ({
      ...prev,
      selectedIds,
    }));
  }, [selectedIds, setUserDialogData]);
  const [isAllColumnPinned, setIsAllColumnPinned] = useState(false);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
    right: ["current_status", "username", "comment", "action"],
  });

  useEffect(() => {
    setPinnedColumns((prevState) => {
      const updatedLeft = [
        GRID_CHECKBOX_SELECTION_COL_DEF.field,
        ...keyColumns,
      ];
      const updatedRight = [...prevState.right];

      return {
        left: updatedLeft,
        right: updatedRight,
      };
    });
  }, [keyColumns]);

  const handleMoveColumns = () => {
    setPinnedColumns((prevState) => {
      const columnsToAdd = ["current_status", "username", "comment"];
      const isColumnsAvailable = columnsToAdd.every((item) =>
        prevState.right.includes(item)
      );
      const isKeyColumnAvailable = keyColumns.every((item: any) =>
        prevState.left.includes(item)
      );
      setIsAllColumnPinned(isColumnsAvailable);
      if (isColumnsAvailable && isKeyColumnAvailable) {
        return {
          left: prevState.left.filter((item) => !keyColumns.includes(item)),
          right: prevState.right.filter((item) => !columnsToAdd.includes(item)),
        };
      } else {
        return {
          left: [...prevState.left, ...keyColumns],
          right: [...columnsToAdd, ...prevState.right],
        };
      }
    });
  };
  const [maxHeight, setMaxHeight] = useState<any>(window.innerHeight - 410);

  React.useEffect(() => {
    const handleResize = () => {
      setMaxHeight(window.innerHeight - 410);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const rowHeight =
    filteredDataRows.length < 5
      ? filteredDataRows.length * 60 + 171
      : filteredDataRows.length < 11
      ? filteredDataRows.length * 40 + 100
      : maxHeight + 95;
  const CustomFooter = ({ selectedIds }: any) => (
    <div className="datatable-footer">
      <strong>Selected IDs:</strong> [{selectedIds.join(", ")}]
      <Button
        className="btn-orange btn-dark btn-sm plus-btn-sm"
        onClick={() => setSelectedIds([])}
      >
        Reset
      </Button>
    </div>
  );

  // Note: Removed the logic that was persisting selectedIds across tab/switcher changes
  // The selectedIds state is now managed by the parent component (AllIssueList.tsx)
  // and is automatically cleared when the tab or switcher changes

  return (
    <>
      <>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={
            (dashboardUserDetailsData?.isBackDropVisible ||
              snapShotData.isBackDropVisible) ??
            false
          }
        ></Backdrop>
        <Box
          className="position-relative"
          style={{
            height: pinnedColumns ? `${rowHeight}px` : "auto",
            maxHeight: pinnedColumns ? `${rowHeight}px` : "auto",
            minHeight: pinnedColumns ? `${rowHeight}px` : "auto",
            width: "100%",
          }}
        >
          {filteredDataRows?.length > 0 && (
            <IconButton
              onClick={handleMoveColumns}
              className={`icon-pin-datagrid navbar-icon burger  ${
                isAllColumnPinned ? "active" : ""
              }`}
            >
              <div className="bar bar1"></div>
              <div className="bar bar2"></div>
              <div className="bar bar3"></div>
            </IconButton>
          )}

          <DataGridPremium
            apiRef={apiRef}
            getRowHeight={() => "auto"}
            className="dataTable filterMenuBox1 filterMenuBox hide-progress-icon no-radius pt-0 bdr-top-0 datatable-column-sep checkbox-result-column remove-pinnedColumnHeaders-bg pinnedColumnHeaders-bg-height"
            sx={{
              "&.columnGroupingModel": {
                "& .MuiDataGrid-columnHeaders": {
                  overflow: "visible",
                },
                "& .MuiDataGrid-row": {
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                },
              },
              "& .MuiDataGrid-main": {
                position: "relative",
                "& .MuiDataGrid-columnHeader--filledGroup": {
                  borderBottom: "solid 1px #e0e0e0",
                  "& .MuiDataGrid-withBorderColor": {
                    borderColor: "transparent",
                  },
                  "& + .MuiDataGrid-columnHeader--filledGroup ": {
                    "&:before": {
                      content: '""',
                      background: "#e0e0e0",
                      width: "1px",
                      height: "1000px",
                      position: "absolute",
                      top: "0px",
                      left: "0px",
                      zIndex: "9999999",
                    },
                  },
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#146FD0",
                fontWeight: 700,
              },
              "& .MuiDataGrid-overlayWrapperInner": {
                // height: "auto !important", //commented because its resizing the overlay window backdrop widow

                "& .MuiDataGrid-overlay": {
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                },
              },
              "& .MuiDataGrid-footerContainer": {},
              minHeight: "100px",
              // disable sort arrow in header
              "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                {},
              "& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainerContent .MuiCheckbox-root":
                { display: "none" },
              "& .MuiDataGrid-menuIcon": {
                display: "none",
              },
              "& .MuiDataGrid-sortIcon": {},
              "& .MuiDataGrid-virtualScroller": {
                minHeight: "100px",
                maxHeight: `calc(100vh - 280px)`,
                overflowY: "auto !important",
                overflowX: { md: "auto" },
              },
            }}
            rows={filteredDataRows || []}
            columns={detailViewDataColumns}
            checkboxSelection={true}
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(selectionModel) => {
              if (selectedIds) {
                const currentSelection = selectionModel;
                if (currentSelection.length > selectedIds.length) {
                  setSelectedIds((prevSelectedIds: any) => {
                    const updatedSelectedIds = new Set(prevSelectedIds);
                    selectionModel.forEach((id: any) => {
                      return updatedSelectedIds.add(id);
                    });
                    return Array.from(updatedSelectedIds);
                  });
                } else {
                  const deselectedId = selectedIds.find(
                    (id: any) => !currentSelection.includes(id)
                  );
                  setSelectedIds((prev: any) => {
                    return prev.filter((item: any) => item !== deselectedId);
                  });
                }
              }
            }}
            //checkboxSelection={checkboxSelection}
            //rowSelectionModel={selectedIds}
            loading={isLoading}
            getRowId={(row: GridRowModel) => row?.id}
            //autoHeight={true}
            disableColumnFilter={true}
            disableColumnSelector={true}
            disableColumnMenu={false}
            getRowClassName={(params: any) => {
              return params?.row?.is_derived ? "derived-column" : "";
            }}
            pinnedColumns={filteredDataRows?.length > 0 ? pinnedColumns : {}}
            disableColumnPinning={false}
            disableRowGrouping={true}
            disableAggregation={true}
            slots={{
              footer: () => (
                <DataGridPaginationFooter pageSizeOptions={pageSizeOptions}>
                  {selectedIds && selectedIds.length > 0 ? (
                    <CustomFooter selectedIds={selectedIds} />
                  ) : null}
                </DataGridPaginationFooter>
              ),
            }}
            {...dataGridProps}
          />
        </Box>
      </>

      <DashboardUserDetails
        dashboardUserDetailsData={dashboardUserDetailsData}
        setDashboardUserDetailsData={setDashboardUserDetailsData}
        allUsersList={allUsersList}
        setIsLoading={setIsLoading}
        setDetailViewDataRows={setDetailViewDataRows}
        issueFrom={currentPageType}
      />
      <IssuesListSnapShot
        snapShotData={snapShotData}
        setSnapShotData={setSnapShotData}
      />
      <IncidentUserandStatusDialog
        userDialogData={userDialogData}
        setUserDialogData={setUserDialogData}
        allUsersList={allUsersList}
        setDetailViewDataRows={setDetailViewDataRows}
        issueFrom={currentPageType}
        setSelectedIds={setSelectedIds}
      />
    </>
  );
};

export default IssueListDataGrid;
